// Wave 1 safety: the open-event count on the admin rail's Safety item (and its Settings row on phones). One copy for
// the page (GET /api/admin/safety/count): read when an admin screen loads, every two minutes while an admin is in
// the app, and right after a resolve on Admin > Safety.
import { useEffect, useSyncExternalStore } from "react";
import { api } from "../api";
import { safetyBadge, type SafetyCount } from "../safety";

const EVERY_MS = 2 * 60_000;
const FRESH_MS = 30_000;
let current: SafetyCount | null = null;
let lastAt = 0;
let inFlight: Promise<void> | null = null;
const subs = new Set<() => void>();
const emit = () => subs.forEach((f) => f());

export function setSafetyCount(c: SafetyCount) {
  current = c;
  lastAt = Date.now();
  emit();
}

/** Reads the count again (`force`: even if it's fresh). Failures keep the last count (an older api has no route). */
export function refreshSafetyCount(force = true): Promise<void> {
  if (!force && Date.now() - lastAt < FRESH_MS) return Promise.resolve();
  inFlight ??= api
    .adminSafetyCount()
    .then(setSafetyCount)
    .catch(() => undefined)
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

/** The count while `admin`; `adminScreen` refreshes it as that screen loads. */
export function useSafetyCount(admin: boolean, adminScreen = false): SafetyCount | null {
  const c = useSyncExternalStore(
    (f) => (subs.add(f), () => subs.delete(f)),
    () => current,
  );
  useEffect(() => {
    if (!admin) return;
    void refreshSafetyCount(adminScreen);
    const t = window.setInterval(() => void refreshSafetyCount(false), EVERY_MS);
    return () => window.clearInterval(t);
  }, [admin, adminScreen]);
  return admin ? c : null;
}

export function SafetyBadge({ count }: { count: SafetyCount | null }) {
  const b = safetyBadge(count);
  if (!b) return null;
  return (
    <span className={`safety-badge is-${b.tone}`} aria-label={b.label} title={b.label}>
      {b.text}
    </span>
  );
}
